/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IProductDimensionsWithAvailabilitiesFull, ProductDimensionFull } from '@msdyn365-commerce/commerce-entities';
import MsDyn365, {      // @CUSTOM: Import MsDyn365
    CacheType, createObservableDataAction, getCatalogId, IAction, IActionContext, IActionInput,
    IAny, ICreateActionContext, IGeneric, IRequestContext
} from '@msdyn365-commerce/core';
import {
    ProductDimension,
    ProductDimensionAvailabilitySearchCriteria,
    ProductDimensionValue,
    ProductDimensionValueInventoryAvailability,
    ProductsDataActions
} from '@msdyn365-commerce/retail-proxy';

//----------------------------------------------------------
// @CUSTOM: Import utilities from the module definition
// instead of from individual files.
//----------------------------------------------------------
import {
    ArrayExtensions,
    createInventoryAvailabilitySearchCriteria,
    getDimensionValuesFromQuery,
    getSelectedProductIdFromActionInput,
    QueryResultSettingsProxy,

    // @CUSTOM: New imports
    DimensionTypes,
    setDimensionValuesToQuery,
    convertDimensionTypeToProductDimensionType,
} from '@msdyn365-commerce-modules/retail-actions';

// Also import hidden variant type
import { HiddenVariant } from '../settings/app.settings';
//----------------------------------------------------------

/**
 * Defines the dimension value type for get dimensions for selected variant data action.
 * Includes old API format for backward compatibility and new API info for availabilities for new logic.
 */
export interface IDimensionValueForSelectedVariant extends ProductDimensionValueInventoryAvailability, ProductDimensionValue {}

/**
 * Defines the returned type for get dimensions for selected variant data action.
 * Includes old API format for backward compatibility and new API info for availabilities for new logic.
 */
export interface IDimensionForSelectedVariant extends ProductDimensionFull, IProductDimensionsWithAvailabilitiesFull {}

/**
 * Input class for the getDimensionsForSelectedVariant data action.
 */
export class GetDimensionsForSelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public matchingDimensionValues: ProductDimension[];

    public catalogId?: number;

    public constructor(
        productId: number,
        channelId: number,
        matchingDimensionValues?: ProductDimension[],
        requestContext?: IRequestContext
    ) {
        this.productId = productId;
        this.channelId = channelId;
        this.matchingDimensionValues = matchingDimensionValues || [];

        if (requestContext) {
            this.catalogId = getCatalogId(requestContext);
        }
    }

    /**
     * Cache key.
     * @returns String.
     */
    public getCacheKey: () => string = () => `DimensionsForSelectedVariantInput-${this.productId}-${this.channelId}-${this.catalogId ?? 0}`;

    /**
     * Cache object type.
     * @returns String.
     */
    public getCacheObjectType: () => string = () => 'DimensionsForSelectedVariantInput';

    /**
     * Cache type.
     * @returns String.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * Create input method for the getDimensionsForSelectedVariant data action.
 * @param inputData - The input data passed to the createInput method.
 * @returns - The input for the data action.
 */
const createDimensionsForSelectedVariantInput = (inputData: ICreateActionContext<IGeneric<IAny>>): GetDimensionsForSelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {

        //----------------------------------------------------------
        // @CUSTOM: Inject the target dimension as if it occurred
        // in the URL.
        // ALSO: if in the browser, update the URL
        //----------------------------------------------------------
        // Get current dimensions
        const curDimensions = getDimensionValuesFromQuery(inputData.requestContext.url.requestUrl);

        // Add the dimension and change the URL
        autoSetDimension(curDimensions, inputData.requestContext.app.config.hiddenVariant, inputData.requestContext.url.requestUrl);

        return new GetDimensionsForSelectedVariantInput(
            +productId,
            +inputData.requestContext.apiSettings.channelId,
            curDimensions,         // WAS: getDimensionValuesFromQuery(inputData.requestContext.url.requestUrl)
            inputData.requestContext
        );
        //----------------------------------------------------------
}
    throw new Error('Unable to create DimensionsForSelectedVariantInput, no productId found on module config or query');
};

//==========================================================
// @CUSTOM: Set the target dimension and update the URL
//==========================================================
function autoSetDimension(dimensions: ProductDimension[], autoVariant: HiddenVariant | undefined, currentUrl: URL) {

    // Verify that global config is fully present. If not, abort.
    if (!autoVariant || !autoVariant.name || !autoVariant.defaultValue) {
        return;
    }

    // Convert the target dimension name to an internal dimension type code
    const dimensionType = convertDimensionTypeToProductDimensionType(autoVariant.name.toLowerCase() as DimensionTypes);

    // Search through the list to see if it's already defined
    const targetDimension = dimensions.find(entry => entry.DimensionTypeValue === dimensionType);

    // If the dimension is already set, abort
    // Note that the dimension could be set to an undesired value, but it's probably best to allow manual overrides
    if (targetDimension) {
        return;
    }

    // Add the target dimension to the list
    dimensions.push({
        DimensionTypeValue: dimensionType,
        DimensionValue: {
            RecordId: 0,
            Value: autoVariant.defaultValue
        }
    });

    // If we're in a browser, update the URL
    // I'm not sure if this is needed, but seems like a good safety measure for any other code that checks.
    if (MsDyn365.isBrowser) {
        setDimensionValuesToQuery(currentUrl, dimensions);
    }
}
//==========================================================

/**
 * Converts new API type with availabilities to old API type to preserve backward compatibility.
 * @param dimensions - Dimensions to convert.
 * @returns Dimensions in old API format.
 */
const prepareDimensionsDataResult = (dimensions: IProductDimensionsWithAvailabilitiesFull[]): IDimensionForSelectedVariant[] => {
    return dimensions.map(newDimension => {
        const oldDimension: IDimensionForSelectedVariant = { ...newDimension };
        oldDimension.DimensionValues = ArrayExtensions.validValues(
            newDimension.dimensionValuesWithInventory?.map(value => value.DimensionValue)
        );
        return oldDimension;
    });
};

/**
 * Action Method for the getDimensionsForSelectedVariant Data Action.
 * @param input - Input for the data action.
 * @param context - Action context.
 * @returns Product dimensions with availability.
 */
export async function getDimensionsForSelectedVariantAction(
    input: GetDimensionsForSelectedVariantInput,
    context: IActionContext
): Promise<IDimensionForSelectedVariant[]> {
    const activeProducts = ArrayExtensions.validValues(
        await ProductsDataActions.getByIdsAsync(
            {
                callerContext: context,
                queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
            },
            input.channelId,
            [input.productId],
            null,
            input.catalogId ?? 0
        )
    );

    if (!ArrayExtensions.hasElements(activeProducts)) {
        return [];
    }

    const activeProduct = activeProducts[0];

    if (!ArrayExtensions.hasElements(activeProduct.Dimensions)) {
        return [];
    }

    const promises = activeProduct.Dimensions.map(dimension => {
        const shippingInventoryConfiguration = createInventoryAvailabilitySearchCriteria(context, [], true, undefined, undefined);

        const searchCriteria: ProductDimensionAvailabilitySearchCriteria = {
            RequestedDimensionTypeValue: dimension.DimensionTypeValue,
            MatchingDimensionValues: input.matchingDimensionValues.filter(item => item.DimensionTypeValue !== dimension.DimensionTypeValue),
            DefaultWarehouseOnly: shippingInventoryConfiguration.DefaultWarehouseOnly,
            FilterByChannelFulfillmentGroup: shippingInventoryConfiguration.FilterByChannelFulfillmentGroup,
            DeliveryModeTypeFilterValue: shippingInventoryConfiguration.DeliveryModeTypeFilterValue,
            CatalogId: input.catalogId
        };

        const dimensionValuesPromise = ProductsDataActions.getDimensionValuesWithEstimatedAvailabilitiesAsync(
            { callerContext: context, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context) },
            activeProduct.MasterProductId ? activeProduct.MasterProductId : activeProduct.RecordId,
            searchCriteria
        );
        const fullDimensionPromise = dimensionValuesPromise.then(dimensionValues => {
            const fullDimension: IProductDimensionsWithAvailabilitiesFull = {
                ...dimension,
                dimensionValuesWithInventory: dimensionValues
            };
            return fullDimension;
        });

        return fullDimensionPromise;
    });

    const dimensionValuesResults = await Promise.all(promises);

    return prepareDimensionsDataResult(dimensionValuesResults);
}

/**
 * The GetDimensionsForSelectedVariantDataAction
 * Get the currently selected Variant via the getSelectedVariant data action, and the gets the
 * available product dimensions for the variant via the getDimensionValues RetailServer API.
 */
export const getDimensionsForSelectedVariantActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-dimensions/for-selected-variant',
    action: getDimensionsForSelectedVariantAction as IAction<IDimensionForSelectedVariant[]>,
    input: createDimensionsForSelectedVariantInput
});

export default getDimensionsForSelectedVariantActionDataAction;
